import { useState } from "react";
import { ActivityLog } from "@/pages/admin/ActivityLogs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { ActivityLogDetailsDialog } from "./ActivityLogDetailsDialog";
import { ChevronLeft, ChevronRight, Eye } from "lucide-react";
import { formatDistanceToNow } from "date-fns";

interface ActivityLogTableProps {
  logs: ActivityLog[];
  totalCount: number;
  page: number;
  limit: number;
  onPageChange: (page: number) => void;
}

const categoryColors: Record<string, string> = {
  auth: "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200",
  profile: "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200",
  admin: "bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200",
  security: "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200",
  data: "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200",
  system: "bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200",
};

export const ActivityLogTable = ({
  logs,
  totalCount,
  page,
  limit,
  onPageChange,
}: ActivityLogTableProps) => {
  const [selectedLog, setSelectedLog] = useState<ActivityLog | null>(null);

  const getFullName = (profile: ActivityLog["user_profile"]) => {
    if (!profile) return "Unknown";
    return `${profile.first_name || ""} ${profile.last_name || ""}`.trim() || "Unknown";
  };

  const getInitials = (profile: ActivityLog["user_profile"]) => {
    if (!profile) return "?";
    const first = profile.first_name?.[0] || "";
    const last = profile.last_name?.[0] || "";
    return `${first}${last}`.toUpperCase() || "?";
  };

  const totalPages = Math.ceil(totalCount / limit);

  if (logs.length === 0) {
    return (
      <div className="text-center py-12 text-muted-foreground">
        No activity logs found
      </div>
    );
  }

  return (
    <>
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Time</TableHead>
              <TableHead>User</TableHead>
              <TableHead>Action</TableHead>
              <TableHead>Actor</TableHead>
              <TableHead>Description</TableHead>
              <TableHead className="text-right">Details</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {logs.map((log) => (
              <TableRow key={log.id}>
                <TableCell className="font-mono text-sm">
                  <div className="flex flex-col">
                    <span>{formatDistanceToNow(new Date(log.created_at), { addSuffix: true })}</span>
                    <span className="text-xs text-muted-foreground">
                      {new Date(log.created_at).toLocaleString()}
                    </span>
                  </div>
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-2">
                    <Avatar className="h-8 w-8">
                      <AvatarFallback>{getInitials(log.user_profile)}</AvatarFallback>
                    </Avatar>
                    <span className="text-sm">{getFullName(log.user_profile)}</span>
                  </div>
                </TableCell>
                <TableCell>
                  <Badge
                    className={categoryColors[log.action_category] || categoryColors.system}
                  >
                    {log.action_type.replace(/_/g, " ")}
                  </Badge>
                </TableCell>
                <TableCell>
                  {log.actor_profile ? (
                    <div className="flex items-center gap-2">
                      <Avatar className="h-8 w-8">
                        <AvatarFallback>{getInitials(log.actor_profile)}</AvatarFallback>
                      </Avatar>
                      <span className="text-sm">{getFullName(log.actor_profile)}</span>
                    </div>
                  ) : (
                    <span className="text-sm text-muted-foreground">System</span>
                  )}
                </TableCell>
                <TableCell className="max-w-md truncate">{log.description}</TableCell>
                <TableCell className="text-right">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setSelectedLog(log)}
                  >
                    <Eye className="h-4 w-4" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <div className="flex items-center justify-between mt-4">
        <div className="text-sm text-muted-foreground">
          Showing {page * limit + 1} to {Math.min((page + 1) * limit, totalCount)} of{" "}
          {totalCount} logs
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => onPageChange(page - 1)}
            disabled={page === 0}
          >
            <ChevronLeft className="h-4 w-4" />
            Previous
          </Button>
          <div className="text-sm">
            Page {page + 1} of {totalPages}
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => onPageChange(page + 1)}
            disabled={page >= totalPages - 1}
          >
            Next
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>

      <ActivityLogDetailsDialog
        log={selectedLog}
        open={!!selectedLog}
        onClose={() => setSelectedLog(null)}
      />
    </>
  );
};
